'use client';

import NextImage, { ImageProps as NextImageProps } from 'next/legacy/image';
import { useEffect, useState } from 'react';
import { FocalPoint } from '~/lib';
import styles from './Image.module.scss';
import clsx from 'clsx';
import { resolveImageLoader } from '../../utils/loaders/resolveImageLoader';

export type ImageProps = NextImageProps & {
    disableSkeleton?: boolean;
    focalPoint?: FocalPoint;
};

type OnLoadingComplete = {
    naturalWidth: number;
    naturalHeight: number;
};

export const Image = ({
    src = '',
    width: initialWidth = 0,
    height: initialHeight = 0,
    layout = 'responsive',
    disableSkeleton = false,
    alt = '',
    focalPoint,
    className,
    priority,
    ...rest
}: ImageProps) => {
    // Initialize loading state based on layout and priority
    const [isLoading, setIsLoading] = useState(() => {
        // Don't show loading state for fill layout or priority images and if image is not defined in the CMS
        if (layout === 'fill' || priority) return false;
        if (typeof src !== 'string') return false;

        // For regular images, start with loading state
        return true;
    });

    const [width, setWidth] = useState(initialWidth);
    const [height, setHeight] = useState(initialHeight);

    const isProduct = typeof src === 'string' && src.includes('asset.productmarketingcloud.com');
    const url = isProduct && typeof src === 'string' ? `${src}/preview` : src;

    const shouldSetDimensions = layout !== 'fill';
    const loader = resolveImageLoader(src);

    // Ensure priority images never show loading state
    useEffect(() => {
        if (priority) {
            setIsLoading(false);
        }
    }, [priority]);

    const onLoadHandler = ({ naturalWidth, naturalHeight }: OnLoadingComplete) => {
        const noDimensions = !width || !height;

        if (layout !== 'fill' && isLoading && noDimensions && shouldSetDimensions) {
            setWidth(naturalWidth);
            setHeight(naturalHeight);
        }

        setIsLoading(false);
    };

    return (
        <span
            className={clsx(styles.imageWrapper, className, {
                [styles.loading]: isLoading && !disableSkeleton,
            })}
        >
            <NextImage
                {...rest}
                loader={loader}
                src={url}
                onLoadingComplete={onLoadHandler}
                width={shouldSetDimensions ? width : undefined}
                height={shouldSetDimensions ? height : undefined}
                layout={layout}
                alt={alt}
                objectPosition={
                    focalPoint ? `${focalPoint.left * 100}% ${focalPoint.top * 100}%` : undefined
                }
                priority={priority}
                loading={priority ? 'eager' : 'lazy'}
                fetchPriority={priority ? 'high' : undefined}
            />
        </span>
    );
};
